/**
 * shipmentInsights - Regras puras da linha do tempo (Gantt) da Remessa.
 *
 * Trechos calculados a partir das transições registradas em `shipment_events`
 * (from_status → to_status), nunca de texto da timeline.
 *
 * RELEASE: 2026-10-18 v1.0.0 - Versão inicial (aba Indicadores do modal da Remessa).
 */
import type { StatusSegment } from '@/lib/serviceOrders/insights';

export interface ShipmentStatusEvent { from_status: string | null; to_status: string | null; created_at: string }

export type ShipmentGroup = 'preparation' | 'transport' | 'done' | 'problem';

export const SHIPMENT_GROUP_ORDER: ShipmentGroup[] = ['preparation', 'transport', 'done', 'problem'];
export const SHIPMENT_GROUP_LABEL: Record<ShipmentGroup, string> = {
  preparation: 'Preparação', transport: 'Transporte', done: 'Concluída', problem: 'Problema',
};
/** Cores por grupo (tokens semânticos do tema). */
export const SHIPMENT_GROUP_COLOR: Record<ShipmentGroup, string> = {
  preparation: 'bg-muted-foreground/50', transport: 'bg-info', done: 'bg-success', problem: 'bg-destructive',
};

/** Grupo de ciclo de cada situação da remessa. */
export function shipmentStatusGroup(status: string): ShipmentGroup {
  if (['shipped', 'in_transit'].includes(status)) return 'transport';
  if (status === 'delivered') return 'done';
  if (['lost', 'returned_to_sender', 'cancelled'].includes(status)) return 'problem';
  return 'preparation'; // draft, preparing, ready
}

const ms = (iso: string) => new Date(iso).getTime();

/**
 * Trechos por situação: o primeiro começa na criação (situação de origem da 1ª transição);
 * cada transição abre um novo trecho. A situação atual fica sem fim (end null) até `now`.
 * Sem transições registradas → lista vazia ("Não registrado").
 */
export function shipmentStatusSegments(createdAt: string, events: ShipmentStatusEvent[], now: string = new Date().toISOString()): StatusSegment[] {
  const changes = events.filter(e => e.to_status).sort((a, b) => ms(a.created_at) - ms(b.created_at));
  if (!changes.length) return [];
  const out: StatusSegment[] = [];
  let cur = changes[0].from_status ?? 'draft';
  let start = createdAt;
  for (const e of changes) {
    if (e.to_status === cur) continue;
    out.push({ status: cur, start, end: e.created_at, seconds: Math.max(0, (ms(e.created_at) - ms(start)) / 1000) });
    cur = e.to_status as string; start = e.created_at;
  }
  const final = ['delivered', 'lost', 'returned_to_sender', 'cancelled'].includes(cur);
  // Situação final encerra o ciclo (trecho de 0 s marcando a chegada); demais seguem abertas
  out.push(final ? { status: cur, start, end: start, seconds: 0 } : { status: cur, start, end: null, seconds: Math.max(0, (ms(now) - ms(start)) / 1000) });
  return out;
}
